import React, { useState } from "react";
import { ModalWrapper } from "../../../components/ui";
import { Button } from "../../../components/ui";
import { getSafetyMeetingPDF } from "../../../services/api/safetymeeting";
import { UI_COPY } from "../../../config";
import { useUiText } from "../../../hooks/useUiText";

/**
 * SafetyMeeting component for worker timesheet page.
 *
 * Displays a card prompting the worker to review the weekly safety meeting PDF.
 * When clicked, opens a modal showing the PDF. Worker confirms they've reviewed it,
 * which is required before submitting their timesheet.
 *
 * @param {string} weekEnd - The week ending date (YYYY-MM-DD)
 * @param {boolean} isReviewed - Whether the safety meeting has been reviewed
 * @param {function} setIsReviewed - Callback to update the reviewed status
 * @returns JSX component
 */
export default function SafetyMeeting({ weekEnd, isReviewed, setIsReviewed }) {
  const { t, language } = useUiText();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch the weekly safety-meeting PDF and open it in the modal.
   *
   * @returns {Promise<void>}
   */
  const handleOpenPDF = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Worker timesheet popup only: Spanish UI gets the ES PDF when present.
      const pdfBlob = await getSafetyMeetingPDF(weekEnd, language);

      // Create object URL from blob
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Error loading safety meeting PDF:", err);
      setError("Failed to load safety meeting PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Mark the meeting reviewed, close the modal, and revoke the blob URL.
   *
   * @returns {void}
   */
  const handleConfirmReviewed = () => {
    setIsReviewed(true);
    setIsModalOpen(false);

    // Clean up object URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  /**
   * Close the modal without marking reviewed, and revoke the blob URL.
   *
   * @returns {void}
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);

    // Clean up object URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return (
    <>
      <div className="w-full">
        <div
          className={`p-0 mt-4 ${isReviewed ? "bg-green-100" : "bg-yellow-100"} rounded-lg border min-h-[36px]`}
        >
          <div className="flex flex-col items-center justify-center">
            {error && (
              <div className="text-black bg-red-200 text-sm mb-2">{error}</div>
            )}
            {isReviewed ? (
              <div className="text-black bg-green-100 text-sm text-center p-2 rounded-md w-full flex items-center justify-center">
                Safety Meeting Reviewed
              </div>
            ) : (
              <Button
                text={t(UI_COPY.timesheet.reviewSafetyMeeting)}
                icon={isLoading ? "spinner" : undefined}
                variant="cautionLight"
                className="w-full"
                onClick={handleOpenPDF}
                disabled={isLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* PDF Modal */}
      <ModalWrapper isOpen={isModalOpen} onClose={handleCloseModal}>
        <div className="flex flex-col h-[80vh] max-h-[800px]">
          {/* Modal Header */}
          <div className="bg-gray-200 p-4 border-b border-gray-300">
            <h2 className="text-xl font-bold text-center">
              {t(UI_COPY.timesheet.weeklySafetyMeeting)}
            </h2>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1 overflow-auto bg-gray-100">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="Safety Meeting PDF"
                className="w-full h-full"
                style={{ minHeight: "500px" }}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Loading PDF...</p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-200 p-4 border-t border-gray-300">
            {!isReviewed && (
              <>
                {/* Disclaimer text */}
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-gray-700 text-center font-medium">
                    {t(UI_COPY.timesheet.safetyMeetingAttestation)}
                  </p>
                </div>

                {/* Confirm button */}
                <Button
                  text={t(UI_COPY.timesheet.confirmReviewed)}
                  variant="primary"
                  className="w-full"
                  onClick={handleConfirmReviewed}
                />
              </>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}
